import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import type { OneOffSessionEpisode } from "@/api/client";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface TranscriptionProgressProps {
  episodes: OneOffSessionEpisode[];
}

function formatTokens(count: number | null): string {
  if (count === null) return "";
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k tokens`;
  }
  return `${count} tokens`;
}

function EpisodeStatus({ episode }: { episode: OneOffSessionEpisode }) {
  return (
    <div
      className={cn(
        "flex items-center justify-between p-4 rounded-xl",
        theme.borders.neubrutalism,
        "bg-white dark:bg-gray-800"
      )}
    >
      <div className="flex items-center gap-3">
        {episode.status === "ready" && (
          <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-500 flex-shrink-0" />
        )}
        {episode.status === "failed" && (
          <XCircleIcon className="w-6 h-6 text-red-600 dark:text-red-500 flex-shrink-0" />
        )}
        {(episode.status === "queued" || episode.status === "in_progress") && (
          <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center">
            <span className="w-5 h-5 border-2 border-gray-400 border-t-primary-orange dark:border-t-primary-orange-dark rounded-full animate-spin" />
          </span>
        )}

        <div className="min-w-0">
          <p className="font-medium text-gray-900 dark:text-white truncate">
            {episode.title}
          </p>
          {episode.status === "failed" && episode.error && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">
              {episode.error}
            </p>
          )}
        </div>
      </div>

      <div className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0 ml-4">
        {episode.status === "ready" && episode.token_count !== null && (
          <span>{formatTokens(episode.token_count)}</span>
        )}
        {episode.status === "in_progress" && (
          <span>Transcribing...</span>
        )}
        {episode.status === "queued" && (
          <span>Queued</span>
        )}
      </div>
    </div>
  );
}

export default function TranscriptionProgress({
  episodes,
}: TranscriptionProgressProps) {
  const readyCount = episodes.filter((e) => e.status === "ready").length;
  const totalCount = episodes.length;
  const progress = totalCount > 0 ? (readyCount / totalCount) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Preparing your episodes...
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          This usually takes just a few seconds per episode
        </p>
      </div>

      <div className="space-y-3">
        {episodes.map((episode) => (
          <EpisodeStatus key={episode.index} episode={episode} />
        ))}
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div
          className={cn(
            "h-3 rounded-full overflow-hidden",
            "bg-gray-200 dark:bg-gray-700"
          )}
        >
          <div
            className="h-full bg-primary-orange dark:bg-primary-orange-dark transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-sm text-center text-gray-600 dark:text-gray-400">
          {readyCount} of {totalCount} ready
        </p>
      </div>
    </div>
  );
}
